import { AxiosResponse } from 'axios';
import React from 'react';
import fileExtension from '../../../infrastructure/constants/fileExtension';
import downloadHelper from '../../../infrastructure/helpers/common/downloadHelper';
import UiDataGrid from '../../Common/DataGrid/UiDataGrid/UiDataGrid';
import FilePicker from '../../Common/FilePicker/FilePicker';
import Modal from '../../Common/Modal/Modal';
import httpClient from '../../../infrastructure/helpers/common/httpClient';
import ColumnDefinition from '../../../infrastructure/types/DataGrid/ColumnDefinition';
import FunctionDefinition from '../../../types/functions/FunctionDefinition';
import functionHelper from '../../../infrastructure/helpers/functions/function/functionHelper';
import typeHelper from '../../../infrastructure/helpers/common/typeHelper';
import stringHelper from '../../../infrastructure/helpers/common/stringHelper';

interface State {
    data: string[];
    error: string;
    title: string;
    isOpen: boolean;
}

interface Props {
    onSaveClick: (functionArguments: string[]) => void;
    functionDefinition: FunctionDefinition;
    maxArrayArgumentCount: number;
}

class FunctionArgumentsModal extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);

        this.state = {
            title: 'Function parameters values',
            isOpen: true,
            error: '',
            data: []
        };

        this.onFilePicked = this.onFilePicked.bind(this);
        this.onReadCsvSuccess = this.onReadCsvSuccess.bind(this);
        this.onDownloadClick = this.onDownloadClick.bind(this);
        this.onClearClick = this.onClearClick.bind(this);
        this.close = this.close.bind(this);
        this.onSaveClick = this.onSaveClick.bind(this);
    }

    onFilePicked(files: FileList, error: string): void {

        this.setState({ error: error });

        if (error) {
            return;
        }

        this.readCsv(files.item(0) as File).then(this.onReadCsvSuccess);
    }

    readCsv(file: File): Promise<AxiosResponse<string[]>> {

        let data = new FormData();

        data.append('file', file);

        return httpClient.postFile('report/read-csv-values', data)
    };

    onReadCsvSuccess(response: AxiosResponse<string[]>): void {

        let data = this.prepareData(response.data);

        this.setState({ data });
    }

    prepareData(data: string[]): string[] {

        let result: string[] = [];

        data.forEach((value) => {
            if (typeHelper.isBoolean(value)) {
                result.push(stringHelper.firstLetterToUpperRestToLower(value));
            }
            else {
                result.push(value);
            }
        });

        return result;
    }

    onDownloadClick(): void {

        let blob = new Blob(this.state.data, { type: 'text/csv' });

        downloadHelper.download(blob, `${this.state.title}.csv`);
    }

    onClearClick(): void {

        this.setState({ data: [] });
    }

    close(): void {
        this.setState({ isOpen: false });
    }

    onSaveClick(): void {

        let error = this.validateFunctionArguments(this.props.functionDefinition, this.state.data);

        if (error) {
            this.setState({ error });
        }
        else {
            this.props.onSaveClick(this.state.data);
        }
    }

    validateFunctionArguments(definition: FunctionDefinition, args: string[]): string {

        let isArrayFunction = this.props.functionDefinition.arguments.some(x => x.isArray);

        if (isArrayFunction && args.length > this.props.maxArrayArgumentCount) {
            return `Maximum arguments count is ${this.props.maxArrayArgumentCount}. Please change the amount of lines in the csv file.`;
        }

        return functionHelper.validateFunctionDefinitionArguments(definition, args);
    }

    render(): JSX.Element {

        return (
            <Modal state={this.state} close={this.close} size="large" key='arguments'>
                <>
                    <UiDataGrid
                        columns={[{
                            header: 'Values',
                            getContent: (item: string) => item,
                        } as ColumnDefinition<string>] as ColumnDefinition<string>[]}
                        data={this.state.data}
                        getKey={(x, i) => i} />

                    <div className="mt-4">
                        <FilePicker
                            uniqueName="function-arguments"
                            allowedExtensions={[fileExtension.csv]}
                            maxFileCount={1}
                            maxFileSizeInMb={5}
                            autoReset={true}
                            height={100}
                            onPicked={this.onFilePicked} />
                    </div>

                    <div className="mt-3">
                        <button className="btn cb-btn" onClick={this.onSaveClick}>Save</button>
                        <button className="btn btn-light" onClick={this.onDownloadClick}>Download</button>
                        <button className="btn btn-light" onClick={this.onClearClick}>Clear</button>
                    </div>
                </>
            </Modal>
        );
    }
}

export default FunctionArgumentsModal;
